// TODO(gk): don't let this file get too big
export interface AutocompleteSymbol {
  kind: "symbol";
  value: string;
}

export interface AutocompleteLiteral {
  kind: "literal";
  value: string;
}

export interface AutocompleteAny {
  kind: "any";
}

export type AutocompleteSuggestion =
  | AutocompleteSymbol
  | AutocompleteLiteral
  | AutocompleteAny;

export interface AutocompleteInfo {
  predicates: string[];
  suggestions: Record<string, AutocompleteSuggestion[][]>;
  actors: AutocompleteSuggestion[];
  resources: AutocompleteSuggestion[];
  extends: Record<string, string[]>;
}

export enum OnboardingStage {
  CreatePolicy = "CreatePolicy",
  InsertFact = "InsertFact",
  CreateApiKey = "CreateApiKey",
  MakeApiRequest = "MakeApiRequest",
  Complete = "Complete",
}

export interface EnvData {
  name: string;
  token: string;
  pub_id: string;
  recovered_at: string | null;
  flag_decisions: Record<string, boolean>;
  onboarding_stage: OnboardingStage | null;
  actions: string[];
  protected: boolean;
}

export interface UserData {
  id: string;
  email?: string;
  name?: string;
  login_identifier: string;
  onboarding_required: boolean;
  pending_invitations: PendingInvitation[];
  created_at: string;
}

export interface PendingInvitation {
  invited_by_username: string | null;
  tenant: {
    id: string;
    name: string;
  };
}

export interface Tenant {
  id: string;
  name: string;
  environments: EnvData[];
  kind: "production" | "sandbox";
  authentication_provider: "github" | "gsuite" | "oidc";
  flag_decisions: Record<string, boolean>;
  actions: string[];
  sandbox_owner_name?: string;
  sandbox_owned_by_current_user: boolean;
  max_users: number | null;
}

export interface Fact {
  predicate: string;
  args: TypedId[];
}

export interface TypedId {
  type: string;
  id: string;
}

export interface Token {
  pub_id: string;
  created_at: string;
  access_level: AccessLevel;
  last_seen_at: string | null;
  name: string | null;
}

export enum AccessLevel {
  ReadWrite = "ReadWrite",
  ReadOnly = "ReadOnly",
}

export type PolicyVersion = {
  id: number;
  created_at: string;
};

export type PolicyStats = {
  /** ISO-8601 timestamps in reverse chron order */
  versions: PolicyVersion[];
};

export type FactStats = {
  total: number;
  predicates: { [predicate: string]: number };
};

export type Stats = {
  policy: PolicyStats;
  facts: FactStats;
};

export type QueryStats = {
  count: number;
};

export type Variable =
  | {
      kind: "TypedId";
      value: { type: string; id: string };
    }
  | {
      kind: "TypedVariable";
      value: { type: string };
    }
  | {
      kind: "FreeVariable";
    };
export interface VariableFact {
  predicate: string;
  args: Variable[];
}
export interface Query {
  context_facts: Fact[];
  fact: VariableFact;
}

interface QueryFields {
  _type: "query";
  duration_ms: number;
  environment: string;
  query: Query;
  request_id: string;
  result_count?: number;
  sql_query?: string;
}

type LogFields = QueryFields;

export interface Log {
  id: string;
  fields: LogFields;
  timestamp: string;
}

// TODO: (@patrickod|@gj) deprecate this duplication and import our other
// Rust-based parser via WASM
/** @deprecated Parses based on OLD object literal syntax */
export function extractLegacyTypedId(token: string): TypedId {
  // pass quoted terms as String types
  const matches = removeSurroundingQuotes(token);
  if (matches) {
    return { type: "String", id: matches.replaceAll('"', "") };
  }

  const [first, ...rest] = token.split(":");

  if (rest.length > 0) {
    return { type: first, id: rest.join(":") };
  } else {
    // NOTE(gj): unsure of the point of this `replaceAll`; leaving it for now.
    return { type: "String", id: first.replaceAll('"', "") };
  }
}

export type Result<T, E> = { ok: true; value: T } | { ok: false; error: E };

function removeSurroundingQuotes(s: string): string | null {
  const matches = s.match(/^"(.+)"$/);
  return matches ? matches[1] : null;
}

/** Loosely based on lexer.rs and polar.lalrpop */
export function extractTypedId(token: string): TypedId {
  // first, if it's quoted, interpret it as a String
  {
    const id = removeSurroundingQuotes(token);
    if (id !== null) {
      return { type: "String", id };
    }
  }

  // next, check if it matches the Type{id} syntax, and if so, interpret it that way
  {
    const matches = token.match(/^(.+){(.+)}$/);
    if (matches) {
      const type = matches[1];
      const id = removeSurroundingQuotes(matches[2]) ?? matches[2];
      return { type, id };
    }
  }
  // now we know it isn't quoted and isn't a Type{id}.

  // next, try to interpret it as a number
  if (token.match(/^[0-9]/)) {
    return { type: "Integer", id: token };
  }
  // next, try to interpret it as a Boolean.
  // if that doesn't work, just interpret it as a String.
  switch (token) {
    case "true":
      return { type: "Boolean", id: "true" };
    case "false":
      return { type: "Boolean", id: "false" };
    default:
      return { type: "String", id: token };
  }
}

export function typedIdsEqual(a: TypedId, b: TypedId): boolean {
  return a.type === b.type && a.id === b.id;
}

/** [[start_row, start_column], [end_row, end_column]] */
export type SourceRange = [[number, number], [number, number]];

/** returned by the /test_policy endpoint */
export interface PolicyTestResult {
  success: boolean;
  errors: PolicyError[];
  tests: TestSummary[];
}

export interface TestSummary {
  name: string;
  passed: number;
  total: number;
  passed_assertions: SourceRange[];
  failed_assertions: SourceRange[];
}

export interface PolicyError {
  error_type: string;
  message: string;
  message_for_editors: string;
  position?: SourceRange;
  test_name?: string;
}

export type NonEmptyArray<T> = [T, ...T[]];
export type Option<T> = T | null;
